<template>
  <BasePopup v-model="visible" class="map-view">
    <template #title>
      <BaseTypography type="h6" weight="700">
        Местоположение
      </BaseTypography>
    </template>
    <template #body>
      <div class="map-view__title">
        <BaseTypography type="text" weight="700">
          Адрес
        </BaseTypography>
      </div>
      <div class="map-view__address-display">
        <BaseTypography v-if="isGeocoding" class="map-view__address-display--placeholder" type="caption">
          Определение адреса...
        </BaseTypography>
        <BaseAddressInput
          v-else-if="currentAddress"
          :model-value="currentAddress"
          class="address-input"
          @select="selectedButton"
        />
        <BaseTypography v-else type="caption">
          Переместите маркер для выбора адреса
        </BaseTypography>
      </div>
      <BaseDivider height="2px" color="var(--color-surface-placeholder)" class="map-view__divider" />
      <div class="map-view__wrapper">
        <YandexMap
          :settings="{
            location: {
              center: markerCoordinates,
              zoom: 17,
              duration: 750,
            },
          }"
          width="532px"
          height="332px"
          class="map-view"
        >
          <YandexMapDefaultSchemeLayer />
          <YandexMapDefaultFeaturesLayer />
          <YandexMapDefaultMarker
            v-model="defaultMarker"
            :settings="{
              coordinates: markerCoordinates,
              draggable: true,
              onDragMove,
            }"
          />
        </YandexMap>
      </div>
    </template>
    <template #actions>
      <BaseButton
        variant="primary"
        :disabled="!currentAddress || isGeocoding"
        class="map-view__save-button"
        @click="handleSave"
      >
        <BaseTypography color="white" type="subtitle" weight="700">
          Подтвердить
        </BaseTypography>
      </BaseButton>
    </template>
  </BasePopup>
</template>

<script setup lang="ts">
import type { LngLat } from '@yandex/ymaps3-types'
import type { YMapDefaultMarker } from '@yandex/ymaps3-types/packages/markers'
import type { DaDataApi } from 'dadata-api'
import { debounce } from 'perfect-debounce'
import { ref, shallowRef, triggerRef } from 'vue'
import {
  YandexMap,
  YandexMapDefaultFeaturesLayer,
  YandexMapDefaultMarker,
  YandexMapDefaultSchemeLayer,
} from 'vue-yandex-maps'
import { BaseAddressInput, BaseButton, BaseDivider, BasePopup, BaseTypography } from '~/shared/ui'

interface Props {
  initialCoordinates?: [number, number]
}

const props = withDefaults(defineProps<Props>(), {
  initialCoordinates: () => [37.617644, 55.755819],
})

const emit = defineEmits<{
  'update:coordinates': [coordinates: [number, number]]
  'update:address': [address: string]
  'save': [data: { coordinates: [number, number], address: string }]
}>()

const defaultMarker = shallowRef<YMapDefaultMarker | null>(null)
const visible = defineModel<boolean>('visible', { default: false })
const markerCoordinates = ref<LngLat>(props.initialCoordinates)
const currentAddress = ref<string>()
const isGeocoding = ref(false)

async function geocodeCoordinates(coordinates: LngLat) {
  isGeocoding.value = true
  try {
    const requestBody = {
      lat: coordinates[1],
      lon: coordinates[0],
      radius_meters: 10,
    }

    interface DaDataGeoResponse {
      suggestions: DaDataApi.Suggestion<DaDataApi.Address>[]
    }

    const { data } = await useFetch<DaDataGeoResponse>('/api/geoToAddress', {
      method: 'POST',
      body: JSON.stringify(requestBody),
    })

    if (data.value?.suggestions && data.value.suggestions.length > 0) {
      currentAddress.value = data.value.suggestions[0].value
    }
    else {
      currentAddress.value = 'Адрес не найден'
    }
  }
  catch (error) {
    console.error('Ошибка геокодирования:', error)
    currentAddress.value = 'Не удалось определить адрес'
  }
  finally {
    isGeocoding.value = false
  }
}

function selectedButton(item: DaDataApi.Suggestion<DaDataApi.Address> | null) {
  if (item && item.data.geo_lat && item.data.geo_lon) {
    currentAddress.value = item.value

    markerCoordinates.value = [
      Number.parseFloat(item.data.geo_lon),
      Number.parseFloat(item.data.geo_lat),
    ]
  }
}

function DragMove() {
  triggerRef(defaultMarker)
  if (defaultMarker.value?.coordinates) {
    markerCoordinates.value = defaultMarker.value.coordinates
    emit('update:coordinates', markerCoordinates.value as [number, number])
    geocodeCoordinates(markerCoordinates.value)
  }
}

const debouncedDragMove = debounce(DragMove, 500)

function onDragMove() {
  debouncedDragMove()
}

function handleSave() {
  emit('save', {
    coordinates: markerCoordinates.value as [number, number],
    address: currentAddress.value as string,
  })
  visible.value = false
}

onMounted(() => {
  geocodeCoordinates(markerCoordinates.value)
})
</script>

<style lang="scss" scoped>
.marker {
  position: relative;
  width: 24px;
  height: 24px;
  background: var(--color-surface-danger);
  border-radius: 50%;
  border: 3px solid #fff;
  box-shadow: 0 2px 8px rgb(0 0 0 / 25%);
  cursor: grab;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.1);
  }

  &:active {
    cursor: grabbing;
    transform: scale(0.95);
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 8px solid var(--color-surface-danger);
  }
}

.map-view {
  border-radius: rem(12px);
  overflow: hidden;

  &__title {
    display: flex;
    padding-top: rem(12px);
  }

  &__wrapper {
    position: relative;
    padding-top: rem(12px);
  }

  &__save-button {
    border-radius: rem(14px);
  }

  &__address-display {
    width: 100%;
    background-color: var(--color-surface-bg);
    border-radius: rem(8px);
    min-height: rem(32px);
    display: flex;
    align-items: center;
    padding: rem(8px) 0 rem(12px);

    &--placeholder {
      height: 38px;
      display: flex;
      align-items: center;
    }

    &:deep(.base-typography) {
      color: var(--color-foreground-regular);
      font-size: rem(14px);
    }

    .address-input {
      width: 100%;
    }
  }
}
</style>
